import api from './api';

export interface CreateIdentityDisclosureRequest {
  recipientUsername: string;
  requestIdentityPhotos: boolean;
  requestMailingAddress: boolean;
}

export interface CreateIdentityDisclosureResponse {
  requestId: string;
  chargedAmount: number;
  status: string;
  recipientUsername: string;
  createdAtUtc: string;
}

export interface IdentityDisclosureEvidence {
  verifiedFullName?: string;
  idState?: string;
  idCountry?: string;
  portraitImageUrl?: string;
  livenessVideoUrl?: string;
  mailingAddressLine1?: string;
  mailingAddressLine2?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingPostalCode?: string;
  mailingCountryCode?: string;
}

export interface IdentityDisclosureRequestDetails {
  requestId: string;
  status: string;
  createdAtUtc: string;
  recipientViewedAtUtc?: string;
  decisionAtUtc?: string;
  requestIdentityPhotos: boolean;
  requestMailingAddress: boolean;
  feeAmount: number;
  requesterUsername: string;
  requesterDisplayName: string;
  recipientUsername: string;
  decisionNotes?: string;
  canDecide: boolean;
  isRequesterView: boolean;
  evidence?: IdentityDisclosureEvidence;
}

export interface IdentityDisclosureDecisionRequest {
  approve: boolean;
  notes?: string;
}

export interface IdentityDisclosurePreference {
  autoApproveAllRequests: boolean;
}

export interface IdentityDisclosureShieldSnapshot {
  recipientUsername: string;
  approvedAtUtc: string;
  requestId: string;
  requestIdentityPhotos: boolean;
  requestMailingAddress: boolean;
  evidence: IdentityDisclosureEvidence;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const toShieldSnapshot = (
  recipientUsername: string,
  details: IdentityDisclosureRequestDetails
): IdentityDisclosureShieldSnapshot | null => {
  if ((details.status || '').toUpperCase() !== 'APPROVED' || !details.evidence) {
    return null;
  }

  return {
    recipientUsername,
    approvedAtUtc: details.decisionAtUtc || details.createdAtUtc,
    requestId: details.requestId,
    requestIdentityPhotos: details.requestIdentityPhotos,
    requestMailingAddress: details.requestMailingAddress,
    evidence: details.evidence,
  };
};

export async function resolveApprovedShieldSnapshot(
  recipientUsername: string,
  requestId?: string
): Promise<IdentityDisclosureShieldSnapshot | null> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const snapshot = await identityDisclosureService.getShieldSnapshot(recipientUsername);
    if (snapshot) {
      return snapshot;
    }

    if (requestId) {
      try {
        const details = await identityDisclosureService.getRequest(requestId);
        const fromRequest = toShieldSnapshot(recipientUsername, details);
        if (fromRequest) {
          return fromRequest;
        }
      } catch {
        // Snapshot may not be readable yet; retry below.
      }
    }

    if (attempt < 3) {
      await wait(400);
    }
  }

  return null;
}

const identityDisclosureService = {
  listMyRequests: () =>
    api.get<IdentityDisclosureRequestDetails[]>('/api/IdentityDisclosure/requests/me'),

  createRequest: (request: CreateIdentityDisclosureRequest) =>
    api.post<CreateIdentityDisclosureResponse>('/api/IdentityDisclosure/requests', request),

  getRequest: (requestId: string) =>
    api.get<IdentityDisclosureRequestDetails>(`/api/IdentityDisclosure/requests/${requestId}`),

  decide: (requestId: string, request: IdentityDisclosureDecisionRequest) =>
    api.post<IdentityDisclosureRequestDetails>(`/api/IdentityDisclosure/requests/${requestId}/decision`, request),

  getPreferences: () =>
    api.get<IdentityDisclosurePreference>('/api/IdentityDisclosure/preferences'),

  updatePreferences: (request: IdentityDisclosurePreference) =>
    api.put<IdentityDisclosurePreference>('/api/IdentityDisclosure/preferences', request),

  getShieldSnapshot: async (recipientUsername: string): Promise<IdentityDisclosureShieldSnapshot | null> => {
    const data = await api.getAllowingNoContent<IdentityDisclosureShieldSnapshot>(
      `/api/IdentityDisclosure/shield/${encodeURIComponent(recipientUsername)}`
    );
    return data?.recipientUsername ? data : null;
  },

  getMyVerificationPreview: () =>
    api.get<IdentityDisclosureEvidence>('/api/IdentityDisclosure/preview/me')
};

export default identityDisclosureService;
